import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { Label } from "@bcc/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@bcc/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@bcc/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bcc/components/ui/tabs";
import { FileSpreadsheet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setInvoiceDetailDrawerTestId } from "./invoice-detail-drawer.utils";

interface LineItem {
  id: string;
  purchaseOrderNumber: string;
  itemDescription: string;
  quantity: number;
  approvedAmount: number;
  shippingFee?: number;
  shipDate: string;
}

interface AuditLogEntry {
  id: string;
  action: string;
  actor: string;
  role: string;
  timestamp: string;
}

interface Invoice {
  id: string;
  amountEUR: number;
  status: "Paid" | "Unpaid" | "Overdue" | "Unposted";
  invoiceDate: string;
  dueDate: string;
  payDate?: string;
  ordersCount: number;
  paymentStatus?: string;
  tax?: {
    vatId?: string;
  };
  country?: string;
}

interface InvoiceDetail {
  lineItems: LineItem[];
  auditLog: AuditLogEntry[];
}

interface InvoiceDetailDrawerProps {
  invoice: Invoice | null;
  invoiceDetail?: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showAuditTab?: boolean;
  isLoading?: boolean;
  testId?: string;
}

export function InvoiceDetailDrawer({
  invoice,
  invoiceDetail,
  open,
  onOpenChange,
  showAuditTab = true,
  isLoading = false,
  testId,
}: InvoiceDetailDrawerProps) {
  const { t } = useTranslation();
  if (!invoice) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const getStatusVariant = (status: Invoice["status"]) => {
    switch (status) {
      case "Paid":
        return "default";
      case "Unpaid":
        return "secondary";
      case "Overdue":
        return "destructive";
      case "Unposted":
        return "outline";
    }
  };

  const handleExportCSV = () => {
    console.log("[bcc] Exporting invoice CSV:", invoice.id);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        testId={testId ?? setInvoiceDetailDrawerTestId("content")}
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{t("components.invoiceDetailDrawer.title")}</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 py-6">
            <div className="h-8 animate-pulse rounded bg-muted" />
            <div className="h-64 animate-pulse rounded bg-muted" />
          </div>
        ) : (
          <Tabs defaultValue="summary" className="mt-6">
            <TabsList
              className={`grid w-full ${showAuditTab ? "grid-cols-3" : "grid-cols-2"}`}
            >
              <TabsTrigger value="summary">
                {t("components.invoiceDetailDrawer.tabs.summary")}
              </TabsTrigger>
              <TabsTrigger value="line-items">
                {t("components.invoiceDetailDrawer.tabs.lineItems")}
              </TabsTrigger>
              {showAuditTab && (
                <TabsTrigger value="audit">
                  {t("components.invoiceDetailDrawer.tabs.auditLog")}
                </TabsTrigger>
              )}
            </TabsList>

            {/* Summary Tab */}
            <TabsContent value="summary" className="space-y-4">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.invoiceId")}
                      </Label>
                      <p className="font-semibold">{invoice.id}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.amount")}
                      </Label>
                      <p className="text-2xl font-bold">
                        {formatAmount(invoice.amountEUR)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.status")}
                      </Label>
                      <div>
                        <Badge variant={getStatusVariant(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.invoiceDate")}
                      </Label>
                      <p>{formatDate(invoice.invoiceDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.dueDate")}
                      </Label>
                      <p>{formatDate(invoice.dueDate)}</p>
                    </div>
                    {invoice.payDate && (
                      <div>
                        <Label className="text-muted-foreground">
                          {t("components.invoiceDetailDrawer.labels.payDate")}
                        </Label>
                        <p>{formatDate(invoice.payDate)}</p>
                      </div>
                    )}
                    {invoice.paymentStatus && (
                      <div>
                        <Label className="text-muted-foreground">
                          {t(
                            "components.invoiceDetailDrawer.labels.paymentStatus",
                          )}
                        </Label>
                        <div>
                          <Badge
                            variant={
                              invoice.paymentStatus === "Reconciled"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {invoice.paymentStatus}
                          </Badge>
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">
                        {t("components.invoiceDetailDrawer.labels.totalOrders")}
                      </Label>
                      <p className="font-semibold">{invoice.ordersCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button
                  onClick={handleExportCSV}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  {t("components.invoiceDetailDrawer.exportCsv")}
                </Button>
              </div>
            </TabsContent>

            {/* Line Items Tab */}
            <TabsContent value="line-items" className="space-y-4">
              <div className="rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.table.poNumber")}
                      </TableHead>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.table.item")}
                      </TableHead>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.table.qty")}
                      </TableHead>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.labels.amount")}
                      </TableHead>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.table.shippingFee")}
                      </TableHead>
                      <TableHead>
                        {t("components.invoiceDetailDrawer.table.shipDate")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceDetail?.lineItems?.map((item: LineItem) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.purchaseOrderNumber}
                        </TableCell>
                        <TableCell>{item.itemDescription}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          {formatAmount(item.approvedAmount)}
                        </TableCell>
                        <TableCell>
                          {formatAmount(item.shippingFee || 0)}
                        </TableCell>
                        <TableCell>{formatDate(item.shipDate)}</TableCell>
                      </TableRow>
                    )) || (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-muted-foreground"
                        >
                          {t("components.invoiceDetailDrawer.noLineItems")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Audit Log Tab */}
            {showAuditTab && (
              <TabsContent value="audit" className="space-y-4">
                <div className="space-y-3">
                  {invoiceDetail?.auditLog?.map((entry: AuditLogEntry) => (
                    <Card key={entry.id}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <p className="font-medium">{entry.action}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{entry.actor}</span>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs">
                                {entry.role}
                              </Badge>
                            </div>
                          </div>
                          <time className="text-sm text-muted-foreground">
                            {new Date(entry.timestamp).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </time>
                        </div>
                      </CardContent>
                    </Card>
                  )) || (
                    <div className="text-center text-muted-foreground py-8">
                      {t("components.invoiceDetailDrawer.noAuditLog")}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
